import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import UserAvatar from '../components/UserAvatar';
import { Button } from '../components/ui/button';
import {
  Hash, Send, Paperclip, ArrowLeft, FileText, X, Loader2, MessageCircle, Search,
  Bell, BellOff, SmilePlus, Pencil, Trash2, Check, CheckCheck, BarChart3,
} from 'lucide-react';
import { ImageLightbox } from '../components/ImageLightbox';
import { PollCard } from '../components/polls/PollCard';
import { CreatePollDialog } from '../components/polls/CreatePollDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const REACTIONS = ['👍', '❤️', '😂', '🎉', '😮', '😢'];

const ChatPage = () => {
  const { user, getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();

  const [channels, setChannels] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState({ type: 'channel', id: 'general', name: 'general' });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [mobileConvOpen, setMobileConvOpen] = useState(false);

  const [mentionIds, setMentionIds] = useState([]);
  const [mentionMenu, setMentionMenu] = useState({ open: false, query: '', start: 0 });
  const [editing, setEditing] = useState(null); // { id, text }
  const [actionsFor, setActionsFor] = useState(null); // message id with open toolbar
  const [reactFor, setReactFor] = useState(null); // message id with open emoji palette
  const [lightbox, setLightbox] = useState(null);
  const [pollOpen, setPollOpen] = useState(false);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const updatePoll = (p) => setMessages((ms) => ms.map((x) => (x.poll_id === p.id ? { ...x, poll: p } : x)));

  const containerRef = useRef(null);
  const stickRef = useRef(true);
  const fileRef = useRef(null);
  const taRef = useRef(null);

  const channelLabel = useCallback(
    (id) => t(`chat.channelNames.${id}`, { defaultValue: id }),
    [t]
  );

  const fetchLists = useCallback(async () => {
    try {
      const [ch, conv] = await Promise.all([
        axios.get(`${API_URL}/chat/channels`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/chat/conversations`, { headers: getAuthHeader() }),
      ]);
      setChannels(ch.data || []);
      setConversations(conv.data || []);
    } catch (e) {
      console.error('chat: failed to load channels/conversations', e);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchLists(); }, [fetchLists]);
  useEffect(() => {
    const id = setInterval(fetchLists, 10000);
    return () => clearInterval(id);
  }, [fetchLists]);

  const fetchMessages = useCallback(async () => {
    if (!active?.id) return;
    try {
      const params = active.type === 'channel'
        ? { scope: 'channel', channel_id: active.id }
        : { scope: 'dm', user_id: active.id };
      const res = await axios.get(`${API_URL}/chat/messages`, { headers: getAuthHeader(), params });
      setMessages(res.data || []);
    } catch (e) {
      console.error('chat: failed to load messages', e);
    }
  }, [active, getAuthHeader]);

  // reset + poll when conversation changes
  useEffect(() => {
    stickRef.current = true;
    setMessages([]);
    setEditing(null);
    setActionsFor(null);
    setReactFor(null);
    fetchMessages();
    const id = setInterval(fetchMessages, 3000);
    return () => clearInterval(id);
  }, [fetchMessages]);

  // Auto-scroll only when the user is already near the bottom (fixes "pull back down")
  useEffect(() => {
    const el = containerRef.current;
    if (el && stickRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const selectConversation = (type, id, name) => {
    stickRef.current = true;
    setActive({ type, id, name });
    setPending([]);
    setText('');
    setMentionIds([]);
    setMobileConvOpen(true);
  };

  // ---- Upload ----
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error(t('chat.fileTooLarge')); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/chat/upload`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setPending((p) => [...p, res.data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('chat.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  // ---- Send ----
  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed && pending.length === 0) return;
    setSending(true);
    const activeMentions = mentionIds.filter((m) => trimmed.includes('@' + m.name)).map((m) => m.id);
    const body = active.type === 'channel'
      ? { scope: 'channel', channel_id: active.id, text: trimmed, attachments: pending, mentions: activeMentions }
      : { scope: 'dm', to_user_id: active.id, text: trimmed, attachments: pending, mentions: activeMentions };
    try {
      stickRef.current = true;
      await axios.post(`${API_URL}/chat/messages`, body, { headers: getAuthHeader() });
      setText('');
      setPending([]);
      setMentionIds([]);
      if (taRef.current) taRef.current.style.height = 'auto';
      await fetchMessages();
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('chat.sendError'));
    } finally {
      setSending(false);
    }
  };

  const autoGrow = (el) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  };

  const onChangeText = (e) => {
    const val = e.target.value;
    setText(val);
    autoGrow(e.target);
    const pos = e.target.selectionStart;
    const sub = val.slice(0, pos);
    const m = sub.match(/(?:^|\s)@([\wÀ-ÿ'-]*)$/);
    if (m) {
      setMentionMenu({ open: true, query: m[1].toLowerCase(), start: pos - m[1].length - 1 });
    } else if (mentionMenu.open) {
      setMentionMenu((mm) => ({ ...mm, open: false }));
    }
  };

  const pickMention = (person) => {
    const pos = taRef.current ? taRef.current.selectionStart : text.length;
    const before = text.slice(0, mentionMenu.start);
    const afterText = text.slice(pos);
    const newText = before + '@' + person.name + ' ' + afterText;
    setText(newText);
    setMentionIds((ids) => (ids.find((x) => x.id === person.user_id) ? ids : [...ids, { id: person.user_id, name: person.name }]));
    setMentionMenu({ open: false, query: '', start: 0 });
    setTimeout(() => { if (taRef.current) { taRef.current.focus(); autoGrow(taRef.current); } }, 0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (mentionMenu.open && mentionCandidates.length > 0) {
        pickMention(mentionCandidates[0]);
      } else {
        handleSend();
      }
    }
    if (e.key === 'Escape') setMentionMenu((mm) => ({ ...mm, open: false }));
  };

  // ---- Reactions / Edit / Delete / Mute ----
  const toggleReaction = async (messageId, emoji) => {
    setReactFor(null);
    setActionsFor(null);
    setMessages((ms) => ms.map((m) => {
      if (m.id !== messageId) return m;
      const reactions = { ...(m.reactions || {}) };
      const users = reactions[emoji] ? [...reactions[emoji]] : [];
      const idx = users.indexOf(user.id);
      if (idx >= 0) users.splice(idx, 1); else users.push(user.id);
      if (users.length) reactions[emoji] = users; else delete reactions[emoji];
      return { ...m, reactions };
    }));
    try {
      await axios.post(`${API_URL}/chat/messages/${messageId}/react`, { emoji }, { headers: getAuthHeader() });
    } catch (e) {
      fetchMessages();
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const newText = editing.text.trim();
    try {
      await axios.put(`${API_URL}/chat/messages/${editing.id}`, { text: newText }, { headers: getAuthHeader() });
      setEditing(null);
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('chat.sendError'));
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm(t('chat.deleteConfirm'))) return;
    setActionsFor(null);
    try {
      await axios.delete(`${API_URL}/chat/messages/${messageId}`, { headers: getAuthHeader() });
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('chat.sendError'));
    }
  };

  const activeMuted = useMemo(() => {
    if (active.type === 'channel') return channels.find((c) => c.id === active.id)?.muted || false;
    return conversations.find((c) => c.user_id === active.id)?.muted || false;
  }, [active, channels, conversations]);

  const toggleMute = async () => {
    const body = active.type === 'channel'
      ? { scope: 'channel', channel_id: active.id, muted: !activeMuted }
      : { scope: 'dm', user_id: active.id, muted: !activeMuted };
    try {
      await axios.post(`${API_URL}/chat/mute`, body, { headers: getAuthHeader() });
      toast.success(!activeMuted ? t('chat.mutedToast') : t('chat.unmutedToast'));
      fetchLists();
    } catch (err) {
      toast.error(t('chat.sendError'));
    }
  };

  // ---- Helpers ----
  // Auth is now carried by the httpOnly cookie, which the browser attaches
  // automatically to same-origin <img>/fetch requests - no token needed here.
  const fileUrl = (path) => `${API_URL}/chat/files/${path}`;

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString(i18n.language === 'de' ? 'de-DE' : 'en-US', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const dayLabel = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    if (sameDay(d, today)) return t('chat.today');
    if (sameDay(d, yest)) return t('chat.yesterday');
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filteredPeople = conversations.filter(
    (c) => !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  const mentionCandidates = useMemo(() => {
    if (!mentionMenu.open) return [];
    return conversations
      .filter((c) => !mentionMenu.query || c.name.toLowerCase().includes(mentionMenu.query))
      .slice(0, 6);
  }, [mentionMenu, conversations]);

  const lastMineId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender_id === user?.id) return messages[i].id;
    }
    return null;
  }, [messages, user]);

  const renderMessageText = (m) => {
    if (!m.text) return null;
    const names = (m.mentions || []).map((x) => x.name);
    if (!names.length) return <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>;
    const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp('(@(?:' + escaped.join('|') + '))', 'g');
    const parts = m.text.split(re);
    const mine = m.sender_id === user?.id;
    return (
      <p className="text-sm whitespace-pre-wrap break-words">
        {parts.map((p, i) => {
          const isMention = p.startsWith('@') && names.includes(p.slice(1));
          return isMention
            ? <span key={i} className={`font-semibold rounded px-0.5 ${mine ? 'bg-white/25' : 'bg-[#FEE2E2] text-[#B91C1C]'}`}>{p}</span>
            : <React.Fragment key={i}>{p}</React.Fragment>;
        })}
      </p>
    );
  };

  const renderReactions = (m) => {
    const entries = Object.entries(m.reactions || {});
    if (!entries.length) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-1" data-testid={`chat-reactions-${m.id}`}>
        {entries.map(([emoji, users]) => {
          const reacted = users.includes(user?.id);
          return (
            <button
              key={emoji}
              onClick={() => toggleReaction(m.id, emoji)}
              data-testid={`chat-reaction-${m.id}-${emoji}`}
              className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs border transition-colors ${reacted ? 'bg-[#FEE2E2] border-[#F5B5B0] text-[#B91C1C]' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            >
              <span>{emoji}</span>
              <span className="font-medium">{users.length}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center text-center text-zinc-400 text-sm px-6">
          {active.type === 'dm' ? t('chat.startConversation', { name: active.name }) : t('chat.noMessages')}
        </div>
      );
    }
    let lastDay = null;
    return (
      <div ref={containerRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-3 md:px-5 py-4 space-y-1" data-testid="chat-messages">
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          const d = dayLabel(m.created_at);
          const showDay = d !== lastDay;
          lastDay = d;
          const showActions = actionsFor === m.id;
          return (
            <React.Fragment key={m.id}>
              {showDay && (
                <div className="flex justify-center my-3">
                  <span className="text-[11px] font-medium text-zinc-500 bg-zinc-100 rounded-full px-3 py-1">{d}</span>
                </div>
              )}
              <div className={`group flex gap-2.5 ${mine ? 'flex-row-reverse' : ''}`} data-testid={`chat-msg-${m.id}`}>
                {!mine && (
                  <div className="shrink-0 mt-0.5">
                    <UserAvatar user={{ id: m.sender_id, name: m.sender_name, avatar_color: m.sender_avatar_color, avatar_url: m.sender_avatar_url }} size="sm" />
                  </div>
                )}
                <div className={`max-w-[80%] md:max-w-[66%] ${mine ? 'items-end' : 'items-start'} flex flex-col relative`}>
                  {!mine && active.type === 'channel' && (
                    <span className="text-[11px] font-semibold text-zinc-600 mb-0.5 ml-1">{m.sender_name}</span>
                  )}

                  {editing?.id === m.id ? (
                    <div className="w-full min-w-[220px]">
                      <textarea
                        value={editing.text}
                        onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                        data-testid={`chat-edit-input-${m.id}`}
                        className="w-full text-sm rounded-xl border border-zinc-300 p-2 focus:outline-none focus:ring-1 focus:ring-[#D93025] resize-none"
                        rows={2}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-1">
                        <Button onClick={saveEdit} data-testid={`chat-edit-save-${m.id}`} className="h-7 rounded-lg bg-[#D93025] hover:bg-[#B91C1C] text-xs px-3">{t('chat.save')}</Button>
                        <Button onClick={() => setEditing(null)} variant="outline" className="h-7 rounded-lg text-xs px-3">{t('chat.cancel')}</Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => { setActionsFor((a) => (a === m.id ? null : m.id)); setReactFor(null); }}
                      className={`relative cursor-pointer ${m.poll ? 'w-[260px] sm:w-[320px] max-w-full' : `rounded-2xl px-3.5 py-2 ${mine ? 'bg-[#D93025] text-white rounded-br-md' : 'bg-white border border-zinc-200 text-zinc-800 rounded-bl-md'}`}`}
                    >
                      {m.poll ? <PollCard poll={m.poll} onChange={updatePoll} onDeleted={fetchMessages} /> : renderMessageText(m)}
                      {(m.attachments || []).map((a) => (
                        <div key={a.path} className={m.text ? 'mt-2' : ''}>
                          {a.is_image || (a.content_type || '').startsWith('image/') ? (
                            <button type="button" data-testid={`chat-image-${a.path}`} onClick={(e) => { e.stopPropagation(); setLightbox({ src: fileUrl(a.path), name: a.name }); }}>
                              <img src={fileUrl(a.path)} alt={a.name} className="rounded-lg max-h-56 w-auto object-cover border border-black/5 cursor-zoom-in" />
                            </button>
                          ) : (
                            <a href={fileUrl(a.path)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${mine ? 'bg-white/15 hover:bg-white/25' : 'bg-zinc-100 hover:bg-zinc-200'}`}>
                              <FileText className="w-4 h-4 shrink-0" />
                              <span className="truncate max-w-[180px]">{a.name}</span>
                            </a>
                          )}
                        </div>
                      ))}

                      {/* Action toolbar */}
                      <div className={`absolute ${mine ? 'left-0 -translate-x-full pr-2' : 'right-0 translate-x-full pl-2'} top-0 flex items-center gap-1 transition-opacity ${showActions ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setReactFor((r) => (r === m.id ? null : m.id)); }}
                          data-testid={`chat-react-btn-${m.id}`}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-zinc-200 text-zinc-500 hover:text-[#D93025] shadow-sm"
                          title={t('chat.react')}
                        >
                          <SmilePlus className="w-3.5 h-3.5" />
                        </button>
                        {mine && m.text && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditing({ id: m.id, text: m.text }); setActionsFor(null); }}
                            data-testid={`chat-edit-btn-${m.id}`}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-zinc-200 text-zinc-500 hover:text-[#D93025] shadow-sm"
                            title={t('chat.edit')}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {mine && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteMessage(m.id); }}
                            data-testid={`chat-delete-btn-${m.id}`}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-zinc-200 text-zinc-500 hover:text-[#D93025] shadow-sm"
                            title={t('chat.delete')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* Emoji palette */}
                      {reactFor === m.id && (
                        <div className={`absolute z-20 -top-10 ${mine ? 'right-0' : 'left-0'} flex items-center gap-1 bg-white border border-zinc-200 rounded-full px-2 py-1 shadow-lg`} data-testid={`chat-emoji-palette-${m.id}`}>
                          {REACTIONS.map((emoji) => (
                            <button key={emoji} onClick={(e) => { e.stopPropagation(); toggleReaction(m.id, emoji); }} className="text-lg hover:scale-125 transition-transform">
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {renderReactions(m)}

                  <div className={`flex items-center gap-1.5 mt-0.5 ${mine ? 'mr-1 flex-row-reverse' : 'ml-1'}`}>
                    <span className="text-[10px] text-zinc-400">{formatTime(m.created_at)}</span>
                    {m.edited_at && <span className="text-[10px] text-zinc-400 italic">{t('chat.edited')}</span>}
                    {mine && m.id === lastMineId && (m.seen_by || []).length > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-[#10B981]" data-testid={`chat-seen-${m.id}`}>
                        <CheckCheck className="w-3.5 h-3.5" />
                        {active.type === 'dm'
                          ? t('chat.seen')
                          : t('chat.seenBy', { names: m.seen_by.map((s) => s.name.split(' ')[0]).join(', ') })}
                      </span>
                    )}
                    {mine && m.id === lastMineId && (m.seen_by || []).length === 0 && (
                      <Check className="w-3.5 h-3.5 text-zinc-300" />
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const ListPane = (
    <div className={`${mobileConvOpen ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-72 shrink-0 bg-white border border-zinc-200 rounded-2xl overflow-hidden`} data-testid="chat-list-pane">
      <div className="overflow-y-auto flex-1">
        <div className="px-4 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wide text-zinc-400">{t('chat.channels')}</div>
        <div className="px-2 pb-2">
          {channels.map((c) => {
            const isActive = active.type === 'channel' && active.id === c.id;
            return (
              <button
                key={c.id}
                onClick={() => selectConversation('channel', c.id, c.id)}
                data-testid={`chat-channel-${c.id}`}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm transition-colors ${isActive ? 'bg-[#D93025] text-white font-semibold' : 'text-zinc-700 hover:bg-zinc-100'}`}
              >
                <Hash className={`w-4 h-4 ${isActive ? '' : 'text-zinc-400'}`} />
                <span className="flex-1 text-left truncate">{channelLabel(c.id)}</span>
                {c.muted ? (
                  <BellOff className={`w-3.5 h-3.5 ${isActive ? 'text-white/70' : 'text-zinc-300'}`} />
                ) : c.unread > 0 ? (
                  <span data-testid={`chat-channel-unread-${c.id}`} className={`min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold ${isActive ? 'bg-white text-[#D93025]' : 'bg-[#D93025] text-white'}`}>{c.unread}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-zinc-400">{t('chat.directMessages')}</div>
        <div className="px-3 pb-2">
          <div className="relative mb-1">
            <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('chat.searchPeople')}
              data-testid="chat-search-people"
              className="w-full text-sm pl-8 pr-2 py-1.5 rounded-lg bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-[#D93025]"
            />
          </div>
        </div>
        <div className="px-2 pb-3">
          {filteredPeople.map((p) => {
            const isActive = active.type === 'dm' && active.id === p.user_id;
            return (
              <button
                key={p.user_id}
                onClick={() => selectConversation('dm', p.user_id, p.name)}
                data-testid={`chat-dm-${p.user_id}`}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm transition-colors ${isActive ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'}`}
              >
                <UserAvatar user={{ id: p.user_id, name: p.name, avatar_color: p.avatar_color, avatar_url: p.avatar_url }} size="sm" />
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium">{p.name}</span>
                    {p.role === 'admin' && <span className={`text-[9px] px-1 rounded ${isActive ? 'bg-white/20' : 'bg-[#FEE2E2] text-[#D93025]'}`}>{t('chat.admin')}</span>}
                    {p.muted && <BellOff className={`w-3 h-3 ${isActive ? 'text-white/60' : 'text-zinc-300'}`} />}
                  </div>
                  {p.last_message && <div className={`text-[11px] truncate ${isActive ? 'text-white/70' : 'text-zinc-400'}`}>{p.last_message}</div>}
                </div>
                {p.unread > 0 && !p.muted && (
                  <span data-testid={`chat-dm-unread-${p.user_id}`} className="shrink-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold bg-[#D93025] text-white">{p.unread}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const ConversationPane = (
    <div className={`${mobileConvOpen ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-[#FAFAFA] border border-zinc-200 rounded-2xl overflow-hidden`} data-testid="chat-conversation-pane">
      <div className="h-14 shrink-0 flex items-center gap-3 px-4 bg-white border-b border-zinc-200">
        <button className="md:hidden" onClick={() => setMobileConvOpen(false)} data-testid="chat-back-btn">
          <ArrowLeft className="w-5 h-5 text-zinc-600" />
        </button>
        {active.type === 'channel' ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#FEE2E2] flex items-center justify-center shrink-0">
              <Hash className="w-4 h-4 text-[#D93025]" />
            </div>
            <span className="font-bold text-zinc-900 truncate">{channelLabel(active.id)}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <UserAvatar user={{ id: active.id, name: active.name }} size="md" />
            <span className="font-bold text-zinc-900 truncate">{active.name}</span>
          </div>
        )}
        <button
          onClick={toggleMute}
          data-testid="chat-mute-btn"
          className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${activeMuted ? 'text-[#D93025] bg-[#FEE2E2]' : 'text-zinc-400 hover:bg-zinc-100'}`}
          title={activeMuted ? t('chat.unmute') : t('chat.mute')}
        >
          {activeMuted ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </button>
      </div>

      {renderMessages()}

      {pending.length > 0 && (
        <div className="px-4 py-2 flex flex-wrap gap-2 bg-white border-t border-zinc-100">
          {pending.map((a) => (
            <div key={a.path} className="flex items-center gap-1.5 bg-zinc-100 rounded-lg pl-2 pr-1 py-1 text-xs">
              {a.is_image ? <img src={fileUrl(a.path)} alt="" className="w-6 h-6 rounded object-cover" /> : <FileText className="w-4 h-4 text-zinc-500" />}
              <span className="truncate max-w-[120px]">{a.name}</span>
              <button onClick={() => setPending((p) => p.filter((x) => x.path !== a.path))} className="text-zinc-400 hover:text-zinc-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="shrink-0 relative p-3 bg-white border-t border-zinc-200">
        {mentionMenu.open && mentionCandidates.length > 0 && (
          <div className="absolute bottom-full left-3 mb-1 w-64 bg-white border border-zinc-200 rounded-xl shadow-lg overflow-hidden z-30" data-testid="chat-mention-menu">
            {mentionCandidates.map((p) => (
              <button
                key={p.user_id}
                onClick={() => pickMention(p)}
                data-testid={`chat-mention-${p.user_id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-100 text-left"
              >
                <UserAvatar user={{ id: p.user_id, name: p.name, avatar_color: p.avatar_color, avatar_url: p.avatar_url }} size="sm" />
                <span className="truncate">{p.name}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.csv,.txt" data-testid="chat-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="chat-attach-btn"
            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl text-zinc-500 hover:bg-zinc-100 transition-colors"
            title={t('chat.attach')}
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setPollOpen(true)}
            data-testid="chat-poll-btn"
            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl text-zinc-500 hover:bg-zinc-100 transition-colors"
            title={t('chat.createPoll')}
          >
            <BarChart3 className="w-5 h-5" />
          </button>
          <textarea
            ref={taRef}
            value={text}
            onChange={onChangeText}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={t('chat.messagePlaceholder')}
            data-testid="chat-message-input"
            className="flex-1 resize-none text-sm rounded-xl border border-zinc-200 px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#D93025] max-h-[140px]"
          />
          <Button
            onClick={handleSend}
            disabled={sending || (!text.trim() && pending.length === 0)}
            data-testid="chat-send-btn"
            className="shrink-0 rounded-xl bg-[#D93025] hover:bg-[#B91C1C] h-10 w-10 p-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="mb-4 flex items-center gap-2">
        <MessageCircle className="w-6 h-6 text-[#D93025]" />
        <h1 className="text-2xl font-bold text-zinc-900">{t('chat.title')}</h1>
      </div>
      <div className="flex gap-4 h-[calc(100vh-200px)] md:h-[calc(100vh-180px)]" data-testid="chat-page">
        {ListPane}
        {ConversationPane}
      </div>
      <ImageLightbox image={lightbox} onClose={closeLightbox} />
      <CreatePollDialog
        open={pollOpen}
        onOpenChange={setPollOpen}
        chatTarget={active.type === 'channel' ? { scope: 'channel', channel_id: active.id } : { scope: 'dm', to_user_id: active.id }}
        onCreated={() => { stickRef.current = true; fetchMessages(); fetchLists(); }}
      />
    </Layout>
  );
};

export default ChatPage;
